// ============================================================================
// Almacén local del navegador (modo contingencia, paso 3 · oct-2026)
//
// IndexedDB y no localStorage porque:
//   - aguanta megas (la carta con descripciones no cabe cómoda en 5 MB junto
//     con todo lo demás que ya guarda la app);
//   - una escritura se da por hecha recién cuando la transacción termina
//     (oncomplete), que es lo que la cola del paso 3d va a necesitar para no
//     decirle "guardado en este equipo" a algo que no lo está.
//
// Almacenes:
//   'copia'                 carta, mesas y pedidos abiertos (paso 3b, versión 1)
//   'pedidos-contingencia'  lo que se hace en la caja de contingencia (paso 3c,
//                           versión 2). NUNCA se borra desde la app: el paso
//                           3d los marca como subidos.
// ============================================================================

const NOMBRE_DB = 'salonika-local';
const VERSION_DB = 2;
const ALMACEN_COPIA = 'copia';
const ALMACEN_PEDIDOS = 'pedidos-contingencia';

let abriendo: Promise<IDBDatabase> | null = null;

function abrir(): Promise<IDBDatabase> {
  if (abriendo) return abriendo;
  abriendo = new Promise<IDBDatabase>((ok, mal) => {
    if (typeof indexedDB === 'undefined') {
      mal(new Error('Este navegador no permite guardar datos locales (IndexedDB).'));
      return;
    }
    const req = indexedDB.open(NOMBRE_DB, VERSION_DB);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(ALMACEN_COPIA)) db.createObjectStore(ALMACEN_COPIA);
      if (!db.objectStoreNames.contains(ALMACEN_PEDIDOS)) db.createObjectStore(ALMACEN_PEDIDOS, { keyPath: 'id' });
    };
    // Otra pestaña con la versión vieja abierta frena la actualización: que
    // no quede colgado esperando.
    req.onblocked = () => mal(new Error('Cierra las otras pestañas de Salonika y recarga.'));
    req.onsuccess = () => ok(req.result);
    req.onerror = () => mal(req.error ?? new Error('No se pudo abrir el almacén local.'));
  });
  // Si falla, el próximo intento vuelve a abrir en vez de quedar con el error.
  abriendo.catch(() => { abriendo = null; });
  return abriendo;
}

export async function leerCopia<T>(clave: string): Promise<T | undefined> {
  const db = await abrir();
  return new Promise<T | undefined>((ok, mal) => {
    const req = db.transaction(ALMACEN_COPIA, 'readonly').objectStore(ALMACEN_COPIA).get(clave);
    req.onsuccess = () => ok(req.result as T | undefined);
    req.onerror = () => mal(req.error);
  });
}

/** Resuelve recién cuando la transacción terminó: ahí sí está escrito. */
export async function guardarCopia<T>(clave: string, valor: T): Promise<void> {
  const db = await abrir();
  return new Promise<void>((ok, mal) => {
    const tx = db.transaction(ALMACEN_COPIA, 'readwrite');
    tx.objectStore(ALMACEN_COPIA).put(valor, clave);
    tx.oncomplete = () => ok();
    tx.onerror = () => mal(tx.error);
    tx.onabort = () => mal(tx.error ?? new Error('Se canceló la escritura local.'));
  });
}

// ---------------------------------------------------------------------------
// Pedidos de la caja de contingencia
// ---------------------------------------------------------------------------
let persistenciaPedida = false;

/** Pide al navegador que no borre estos datos si le falta espacio. */
function pedirPersistencia() {
  if (persistenciaPedida) return;
  persistenciaPedida = true;
  try { void navigator.storage?.persist?.(); } catch { /* el navegador decide */ }
}

export async function listarPedidosContingencia<T>(): Promise<T[]> {
  const db = await abrir();
  return new Promise<T[]>((ok, mal) => {
    const req = db.transaction(ALMACEN_PEDIDOS, 'readonly').objectStore(ALMACEN_PEDIDOS).getAll();
    req.onsuccess = () => ok(req.result as T[]);
    req.onerror = () => mal(req.error);
  });
}

/**
 * Lee, cambia y escribe un pedido en UNA transacción (paso 3d). La subida y el
 * cajero tocan el mismo pedido a la vez: así ninguno pisa lo que hizo el otro,
 * tampoco desde otra pestaña. `cambiar` no puede ser async (la transacción se
 * cerraría). Si lanza, no se escribe nada y el error llega tal cual.
 */
export async function actualizarPedidoContingencia<T extends { id: string }>(id: string, cambiar: (actual: T) => T): Promise<T> {
  pedirPersistencia();
  const db = await abrir();
  return new Promise<T>((ok, mal) => {
    const tx = db.transaction(ALMACEN_PEDIDOS, 'readwrite');
    const almacen = tx.objectStore(ALMACEN_PEDIDOS);
    let nuevo: T | undefined;
    let fallo: unknown = null;
    const req = almacen.get(id);
    req.onsuccess = () => {
      if (req.result === undefined) {
        fallo = new Error('El pedido no está en este equipo.');
        tx.abort();
        return;
      }
      try {
        nuevo = cambiar(req.result as T);
      } catch (e) {
        fallo = e;
        tx.abort();
        return;
      }
      if (nuevo !== req.result) almacen.put(nuevo);
    };
    tx.oncomplete = () => ok(nuevo as T);
    tx.onerror = () => { if (!fallo) mal(tx.error); };
    tx.onabort = () => mal(fallo ?? tx.error ?? new Error('Se canceló la escritura local.'));
  });
}

/** Resuelve recién cuando la transacción terminó. Antes de eso, NO está guardado. */
export async function guardarPedidoContingencia<T extends { id: string }>(p: T): Promise<void> {
  pedirPersistencia();
  const db = await abrir();
  return new Promise<void>((ok, mal) => {
    const tx = db.transaction(ALMACEN_PEDIDOS, 'readwrite');
    tx.objectStore(ALMACEN_PEDIDOS).put(p);
    tx.oncomplete = () => ok();
    tx.onerror = () => mal(tx.error);
    tx.onabort = () => mal(tx.error ?? new Error('Se canceló la escritura local.'));
  });
}
